import axios from 'axios';

const api = axios.create({
  baseURL: '',
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Handle 401 responses — only hard-redirect on protected pages
const PUBLIC_PATHS = ['/', '/login', '/register', '/blog', '/pricing', '/contact', '/terms', '/privacy', '/community', '/join'];
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const currentPath = window.location.pathname;
      const isPublicPage = PUBLIC_PATHS.some(p => currentPath === p || currentPath.startsWith('/blog/') || currentPath.startsWith('/community/'));
      if (!isPublicPage) {
        localStorage.removeItem('jepprep_user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  register: (data) => api.post('/api/auth/register', data),
  login: (data) => api.post('/api/auth/login', data),
  getSession: () => api.get('/api/auth/session'),
  logout: () => api.post('/api/auth/logout'),
  googleCallback: (sessionId) => api.post('/api/auth/google/callback', { session_id: sessionId }),
  completeProfile: (displayName) => api.post('/api/auth/complete-profile', { display_name: displayName }),
  changePassword: (currentPassword, newPassword) => api.post('/api/auth/change-password', { current_password: currentPassword, new_password: newPassword }),
};

// Users
export const usersAPI = {
  getMe: () => api.get('/api/users/me'),
  updateMe: (data) => api.put('/api/users/me', null, { params: data }),
  getMyStats: () => api.get('/api/users/me/stats'),
  getProfile: (userId) => api.get(`/api/users/${userId}/profile`),
  getAvatars: () => api.get('/api/avatars'),
  getAchievements: () => api.get('/api/achievements/me'),
  checkAchievements: () => api.post('/api/achievements/check'),
  updateFeaturedBadges: (badgeIds) => api.put('/api/achievements/featured', { badge_ids: badgeIds }),
};

// Boards
export const boardsAPI = {
  list: (params) => api.get('/api/boards', { params }),
  create: (data) => api.post('/api/boards', data),
  generate: (data) => api.post('/api/boards/generate', data),
  get: (boardId) => api.get(`/api/boards/${boardId}`),
  update: (boardId, data) => api.put(`/api/boards/${boardId}`, data),
  delete: (boardId) => api.delete(`/api/boards/${boardId}`),
  duplicate: (boardId) => api.post(`/api/boards/${boardId}/duplicate`),
  publish: (boardId) => api.post(`/api/boards/${boardId}/publish`),
  soloAnswer: (boardId, data) => api.post(`/api/boards/${boardId}/solo/answer`, data),
  completeGame: (boardId) => api.post(`/api/boards/${boardId}/solo/complete-game`),
  generateRound2: (boardId) => api.post(`/api/boards/${boardId}/generate-round2`),
  stats: () => api.get('/api/boards/stats'),
};

// Game Sessions
export const sessionsAPI = {
  create: (data) => api.post('/api/sessions', data),
  join: (data) => api.post('/api/sessions/join', data),
  get: (sessionId) => api.get(`/api/sessions/${sessionId}`),
  start: (sessionId) => api.post(`/api/sessions/${sessionId}/start`),
  selectClue: (sessionId, categoryIdx, clueIdx) => 
    api.post(`/api/sessions/${sessionId}/select-clue`, null, { params: { category_idx: categoryIdx, clue_idx: clueIdx } }),
  buzz: (sessionId) => api.post(`/api/sessions/${sessionId}/buzz`),
  answer: (sessionId, answer) => api.post(`/api/sessions/${sessionId}/answer`, null, { params: { answer } }),
  wager: (sessionId, wager) => api.post(`/api/sessions/${sessionId}/wager`, null, { params: { wager } }),
  finalAnswer: (sessionId, answer) => api.post(`/api/sessions/${sessionId}/final-answer`, null, { params: { answer } }),
  activateBuzzer: (sessionId) => api.post(`/api/sessions/${sessionId}/activate-buzzer`),
  getResults: (sessionId) => api.get(`/api/sessions/${sessionId}/results`),
};

// Community
export const communityAPI = {
  browse: (params) => api.get('/api/community/boards', { params }),
  getBoard: (boardId) => api.get(`/api/community/boards/${boardId}`),
  rate: (boardId, rating) => api.post(`/api/community/boards/${boardId}/rate`, { rating }),
  review: (boardId, data) => api.post(`/api/community/boards/${boardId}/review`, data),
  fork: (boardId) => api.post(`/api/community/boards/${boardId}/fork`),
  getCreator: (userId) => api.get(`/api/creators/${userId}`),
  shareScore: (boardId, finalScore) => api.post(`/api/community/boards/${boardId}/share-score`, { final_score: finalScore }),
  checkPlayed: (boardId) => api.get(`/api/community/boards/${boardId}/check-played`),
  getLeaderboard: (boardId) => api.get(`/api/community/boards/${boardId}/leaderboard`),
  startChallenge: (boardId) => api.post(`/api/community/boards/${boardId}/start-challenge`),
  completeChallenge: (boardId, finalScore) => api.post(`/api/community/boards/${boardId}/complete-challenge`, { final_score: finalScore }),
};

// Sprint
export const sprintAPI = {
  start: (data) => api.post('/api/sprint/sessions', data),
  answer: (sessionId, data) => api.post(`/api/sprint/sessions/${sessionId}/answer`, data),
  getResults: (sessionId) => api.get(`/api/sprint/sessions/${sessionId}/results`),
  getComprehensiveStats: () => api.get('/api/sprint/stats/comprehensive'),
  getMissedQuestions: (params) => api.get('/api/sprint/missed-questions', { params }),
};

// Daily Challenge
export const dailyChallengeAPI = {
  get: () => api.get('/api/daily-challenge'),
  start: () => api.post('/api/daily-challenge/start'),
  answer: (submissionId, clueIndex, answer, timeTakenMs) => 
    api.post('/api/daily-challenge/answer', { 
      submission_id: submissionId, 
      clue_index: clueIndex, 
      answer, 
      time_taken_ms: timeTakenMs 
    }),
  getLeaderboard: (date) => api.get('/api/daily-challenge/leaderboard', { params: { date } }),
  getWeeklyLeaderboard: () => api.get('/api/daily-challenge/leaderboard/weekly'),
  getAllTimeLeaderboard: () => api.get('/api/daily-challenge/leaderboard/alltime'),
  getRivals: (period = 'today') => api.get('/api/daily-challenge/rivals', { params: { period } }),
  getHistory: () => api.get('/api/daily-challenge/history'),
};

// Study Path
export const studyPathAPI = {
  getRecommendations: () => api.get('/api/study-path/me'),
  getCategories: () => api.get('/api/study-path/categories'),
  startSession: (data) => api.post('/api/study-path/sessions', data),
  answer: (sessionId, data) => api.post(`/api/study-path/sessions/${sessionId}/answer`, data),
  getSession: (sessionId) => api.get(`/api/study-path/sessions/${sessionId}`),
  getResults: (sessionId) => api.get(`/api/study-path/sessions/${sessionId}/results`),
};

// Daily Categories API
export const dailyCategoriesAPI = {
  getTodaysCategories: () => api.get('/api/daily-categories/today'),
  startSession: (data) => api.post('/api/daily-categories/sessions', data),
  submitAnswer: (sessionId, data) => api.post(`/api/daily-categories/sessions/${sessionId}/answer`, data),
  getResults: (sessionId) => api.get(`/api/daily-categories/sessions/${sessionId}/results`),
  getStats: () => api.get('/api/daily-categories/stats'),
};

// Public API (no auth required)
export const publicAPI = {
  getTop3Leaderboard: () => api.get('/api/public/leaderboard/top3'),
};

// Admin
export const adminAPI = {
  _api: api,
  analytics: () => api.get('/api/admin/analytics'),
  listUsers: (params) => api.get('/api/admin/users', { params }),
  getUser: (userId) => api.get(`/api/admin/users/${userId}`),
  disableUser: (userId) => api.put(`/api/admin/users/${userId}/disable`),
  enableUser: (userId) => api.put(`/api/admin/users/${userId}/enable`),
  resetPassword: (userId, newPassword) => api.put(`/api/admin/users/${userId}/reset-password`, { new_password: newPassword }),
  listCommunityBoards: (params) => api.get('/api/admin/community-boards', { params }),
  featureBoard: (boardId) => api.put(`/api/admin/community-boards/${boardId}/feature`),
  unfeatureBoard: (boardId) => api.put(`/api/admin/community-boards/${boardId}/unfeature`),
  deleteUser: (userId) => api.delete(`/api/admin/users/${userId}`),
  removeCommunityBoard: (boardId) => api.delete(`/api/admin/community-boards/${boardId}`),
  systemHealth: () => api.get('/api/admin/system-health'),
  getConfig: () => api.get('/api/admin/config'),
  listBlogPosts: () => api.get('/api/admin/blog/posts'),
  getBlogPost: (postId) => api.get(`/api/admin/blog/posts/${postId}`),
  createBlogPost: (data) => api.post('/api/admin/blog/posts', data),
  updateBlogPost: (postId, data) => api.put(`/api/admin/blog/posts/${postId}`, data),
  deleteBlogPost: (postId) => api.delete(`/api/admin/blog/posts/${postId}`),
};

export default api;
